/**
 * ¿Compraron después del mensaje? La recompra de una campaña a un público de la Base (también el de «Subir un Excel»)
 * o al Público de Activa, contra la base de ventas en uso. Hasta el 27/10/2026 esas campañas decían «Recompra: sin
 * medición»: la agencia veía los clics pero no lo que vendió la campaña.
 *
 * - El backend la calcula cada vez que se abre el resultado (no hay «Medir» ni archivos que subir acá), salvo mientras
 *   la campaña envía: ahí dice «Se mide cuando termina el envío» (`EN_ENVIO`) y se ve el texto tal cual.
 * - Primero la cobertura («4 de 6 están en tu base de ventas»): de los demás no se puede saber si compraron, y la tasa
 *   es sobre los que están.
 * - Sin grupo de comparación (decisión de Alan): no es el efecto del mensaje, y la tarjeta lo dice siempre.
 * - Sin la base de después del envío: qué falta y, para el equipo, el link para subirla (la página es del equipo); una
 *   persona de un cliente lee que la sube el equipo. Misma visibilidad que la medición de Activa: los dos la ven.
 * - Lo simulado no se mide.
 */

import { Link } from "react-router-dom";
import { AlertTriangle, TrendingUp, UploadCloud } from "lucide-react";
import type { GrupoVentas, Medicion } from "../types";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { R_SUBIR_BASE, diaMes } from "../../base-ventas/logic/base";
import { ars } from "../logic/formato";
import { TEXTO_BASE_LA_SUBE_EL_EQUIPO, TEXTO_MEDICION_BASE_SIN_CONTROL, advertenciasMedicion } from "../logic/textos";
import { num, plural } from "../../campana-nueva/utils/format";

const tasaTexto = (t: number) => `${(Math.round((Number(t) || 0) * 1000) / 10).toLocaleString("es-AR")} %`;

/** «el cupón VOLVER» o «los cupones VOLVER y CAMPEON». */
function cuponTexto(codigos: string[]): string {
  if (codigos.length <= 1) return `el cupón ${codigos[0] ?? ""}`;
  return `los cupones ${codigos.slice(0, -1).join(", ")} y ${codigos[codigos.length - 1]}`;
}

interface Props {
  medicion: Medicion | null;
  simulado: boolean;
}

export function MedicionBaseVentas({ medicion, simulado }: Props) {
  const equipo = useEsEquipoDeenex();

  if (simulado || (medicion && !medicion.disponible && medicion.motivo === "SIMULADO")) {
    return (
      <p className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
        Los envíos simulados no se miden: no salió ningún mensaje de verdad.
      </p>
    );
  }

  if (!medicion) {
    return <p className="text-sm text-slate-500">Todavía no se midió quién compró.</p>;
  }

  if (!medicion.disponible || medicion.atribucion !== "base-de-ventas") {
    const faltaLaBase = !medicion.disponible && (medicion.motivo === "BASE_DE_VENTAS_ANTERIOR" || medicion.motivo === "SIN_BASE_DE_VENTAS");
    return (
      <div className="space-y-3" data-testid="medicion-base-vacia">
        <p className="flex items-start gap-2 text-sm text-slate-700">
          <TrendingUp size={15} className="mt-0.5 text-emerald-500 flex-shrink-0" />
          {!medicion.disponible ? medicion.mensaje : "Todavía no se midió quién compró."}
        </p>
        {faltaLaBase && (equipo ? (
          <div className="flex flex-wrap items-center gap-3">
            {!medicion.disponible && medicion.accion && <p className="text-sm text-slate-600">{medicion.accion}</p>}
            <Link to={R_SUBIR_BASE}
              className="min-h-[44px] px-4 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 inline-flex items-center gap-2">
              <UploadCloud size={14} /> Subir la base de ventas
            </Link>
          </div>
        ) : (
          <p className="text-sm text-slate-500">{TEXTO_BASE_LA_SUBE_EL_EQUIPO}</p>
        ))}
      </div>
    );
  }

  // «Los que recibieron» son los que les llegó; si ninguno tiene recibo de entrega, todos los que salieron
  const conRecibo = medicion.entregados.n > 0;
  const g: GrupoVentas = conRecibo ? medicion.entregados : medicion.envio;
  const fuera = g.n - g.enLaBase;
  const hasta = diaMes(medicion.base.hasta);

  return (
    <div className="space-y-4">
      {g.enLaBase === 0 ? (
        <p className="text-sm text-slate-700" data-testid="medicion-cobertura">
          {plural(g.n, "La persona que recibió", `Ninguna de las ${num(g.n)} personas que recibieron`)} el mensaje{" "}
          {plural(g.n, "no está", "está")} en tu base de ventas (ni por celular ni por mail): no se puede saber si {plural(g.n, "compró", "compraron")}.
        </p>
      ) : (
        <>
          <p className="text-sm text-slate-700" data-testid="medicion-cobertura">
            <strong>{num(g.enLaBase)}</strong> de {num(g.n)} {plural(g.n, "persona que recibió", "personas que recibieron")} el mensaje{" "}
            {plural(g.enLaBase, "está", "están")} en tu base de ventas
            {fuera > 0 && <span className="text-slate-500"> · de {plural(fuera, "la otra", `las otras ${num(fuera)}`)} no se puede saber si {plural(fuera, "compró", "compraron")}</span>}
          </p>
          <p className="text-lg text-slate-900" data-testid="medicion-principal">
            <strong className="text-emerald-700">{num(g.volvieron)}</strong> de {num(g.enLaBase)} {plural(g.volvieron, "compró", "compraron")} en los{" "}
            {num(medicion.ventana.dias)} días después de recibirlo ({tasaTexto(g.tasa)})
          </p>
          <p className="text-sm text-slate-600" data-testid="medicion-plata">
            {ars(g.plata)} en {num(g.pedidos)} {plural(g.pedidos, "pedido", "pedidos")} de los que recibieron el mensaje
          </p>
          {medicion.cupon && medicion.cupon.codigos.length > 0 && g.volvieron > 0 && (
            <p className="text-sm text-slate-600" data-testid="medicion-cupon">
              {num(g.usaronCupon)} de los {num(g.volvieron)} {plural(g.usaronCupon, "usó", "usaron")} {cuponTexto(medicion.cupon.codigos)}
            </p>
          )}
        </>
      )}
      <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="medicion-sin-control">
        <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" /> {TEXTO_MEDICION_BASE_SIN_CONTROL}
      </p>
      {advertenciasMedicion(medicion.advertencias).map((a, i) => (
        <p key={i} className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="medicion-advertencia">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" /> {a}
        </p>
      ))}
      {conRecibo && medicion.envio.n > medicion.entregados.n && (
        <p className="text-xs text-slate-500">
          Contando también a los que salieron sin confirmación de entrega: compraron {num(medicion.envio.volvieron)} de {num(medicion.envio.enLaBase)} en tu base de ventas ({tasaTexto(medicion.envio.tasa)}).
        </p>
      )}
      <p className="text-[11px] text-slate-400">
        {hasta ? `Con la base de ventas al ${hasta}` : "Con la base de ventas en uso"} · se vuelve a calcular cada vez que abrís la campaña
      </p>
    </div>
  );
}

export default MedicionBaseVentas;
